import React, { useEffect, useState } from 'react';
import { array, oneOf, string } from 'prop-types';
import { useField } from 'react-final-form';
import classNames from 'classnames';
import { LuArrowRight, LuImage } from 'react-icons/lu';
import { FormattedMessage } from '../../util/reactIntl';
import { getCardPrice } from '../../util/listingPackages';
import { MAX_RELATED_ITEMS, togglePick } from '../../util/relatedListings';
import { NamedLink, PackagePrice, ResponsiveImage } from '../../components';

import css from './RelatedListingsPicker.css';

export const KIND_WORKS = 'works';
export const KIND_SERVICES = 'services';

const VISIBLE_PICKER_ROWS = 6;
const THUMBNAIL_VARIANTS = ['scaled-small', 'scaled-medium'];

// Works: the client name, when there is one. Services: "from {price}", as on
// the service cards.
const ItemMeta = ({ kind, listing }) => {
  const publicData = listing.attributes.publicData || {};
  if (kind === KIND_WORKS) {
    const client = publicData.relatedWorkExperienceCustomer;
    return typeof client === 'string' && client.trim() ? (
      <span className={css.meta}>{client.trim()}</span>
    ) : null;
  }
  const cardPrice = getCardPrice(listing);
  if (!cardPrice) {
    return null;
  }
  return (
    <span className={css.meta}>
      <FormattedMessage id="RelatedListingsPicker.from" />{' '}
      <PackagePrice
        className={css.metaPrice}
        amountClassName={css.metaAmount}
        price={cardPrice.price}
        pricingModel={cardPrice.pricingModel}
      />
    </span>
  );
};

// A row is a label around a hidden checkbox: picked rows show their pick
// number (the order on the service page) instead of the empty box.
const PickerRow = ({ kind, listing, index, pickNumber, disabled, onToggle }) => {
  const title = listing.attributes.title || '';
  const cover = listing.images && listing.images.length > 0 ? listing.images[0] : null;
  const isPicked = pickNumber > 0;
  return (
    <label
      className={classNames(css.row, {
        [css.rowPicked]: isPicked,
        [css.rowDisabled]: disabled,
      })}
    >
      <input
        type="checkbox"
        className={css.checkbox}
        checked={isPicked}
        disabled={disabled}
        onChange={() => onToggle(listing.id.uuid)}
      />
      <span
        className={classNames(css.indicator, { [css.indicatorPicked]: isPicked })}
        aria-hidden="true"
      >
        {isPicked ? pickNumber : null}
      </span>
      <span className={classNames(css.thumbnail, { [css.thumbnailAltTint]: index % 2 === 1 })}>
        {cover ? (
          <ResponsiveImage
            rootClassName={css.thumbnailImage}
            alt=""
            image={cover}
            variants={THUMBNAIL_VARIANTS}
            sizes="96px"
          />
        ) : (
          <LuImage className={css.placeholderIcon} aria-hidden="true" />
        )}
      </span>
      <span className={css.text}>
        <span className={css.title}>{title}</span>
        <ItemMeta kind={kind} listing={listing} />
      </span>
    </label>
  );
};

/**
 * Figma 334:1946 "Related portfolio works" / "Related services": the
 * freelancer picks up to three of their published works or other services.
 * The field value is the picked listing IDs in pick order. Picks that are no
 * longer in `items` don't count and are dropped on the next change. Related
 * services render nothing while loading or without other services.
 */
const RelatedListingsPicker = props => {
  const { name, kind, items, status } = props;
  const { input } = useField(name);
  const [expanded, setExpanded] = useState(false);
  const isWorks = kind === KIND_WORKS;
  const isLoaded = status === 'loaded';
  const hasItems = isLoaded && items.length > 0;

  const itemIds = items.map(listing => listing.id.uuid);
  const value = Array.isArray(input.value) ? input.value : [];
  const selectedIds = value.filter(id => itemIds.includes(id));
  const isFull = selectedIds.length >= MAX_RELATED_ITEMS;
  // A pick past the first rows opens the list, and it stays open: there is no
  // collapse, so unpicking that row doesn't pull it out from under the cursor.
  const hasPickPastVisibleRows = itemIds
    .slice(VISIBLE_PICKER_ROWS)
    .some(id => selectedIds.includes(id));
  useEffect(() => {
    if (hasPickPastVisibleRows) {
      setExpanded(true);
    }
  }, [hasPickPastVisibleRows]);

  if (!isWorks && (status === 'loading' || (isLoaded && items.length === 0))) {
    return null;
  }

  const isExpanded = expanded || hasPickPastVisibleRows;
  const shownItems = isExpanded ? items : items.slice(0, VISIBLE_PICKER_ROWS);
  const prefix = isWorks ? 'works' : 'services';

  return (
    <section className={css.root}>
      <div className={css.header}>
        <div className={css.titleRow}>
          <h2 className={css.sectionTitle}>
            <FormattedMessage id={`RelatedListingsPicker.${prefix}Title`} />
          </h2>
          {hasItems ? (
            <span className={css.count}>
              <FormattedMessage
                id="RelatedListingsPicker.count"
                values={{ count: selectedIds.length, max: MAX_RELATED_ITEMS }}
              />
            </span>
          ) : null}
        </div>
        <p className={css.description}>
          <FormattedMessage
            id={`RelatedListingsPicker.${prefix}Description`}
            values={{ max: MAX_RELATED_ITEMS }}
          />
        </p>
      </div>

      {status === 'error' ? (
        <p className={css.error} role="alert">
          <FormattedMessage id="RelatedListingsPicker.loadError" />
        </p>
      ) : null}

      {isLoaded && isWorks && items.length === 0 ? (
        <div className={css.empty}>
          <p className={css.emptyText}>
            <FormattedMessage id="RelatedListingsPicker.worksEmpty" />
          </p>
          <NamedLink className={css.emptyLink} name="NewPortfolioWorkPage" target="_blank">
            <FormattedMessage id="RelatedListingsPicker.worksEmptyLink" />
            <LuArrowRight className={css.arrow} aria-hidden="true" />
          </NamedLink>
        </div>
      ) : null}

      {hasItems ? (
        <div className={css.list}>
          <div className={css.grid}>
            {shownItems.map((listing, index) => {
              const pickNumber = selectedIds.indexOf(listing.id.uuid) + 1;
              return (
                <PickerRow
                  key={listing.id.uuid}
                  kind={kind}
                  listing={listing}
                  index={index}
                  pickNumber={pickNumber}
                  disabled={isFull && pickNumber === 0}
                  onToggle={id => input.onChange(togglePick(selectedIds, id))}
                />
              );
            })}
          </div>
          {isFull ? (
            <p className={css.limitNote}>
              <FormattedMessage
                id={`RelatedListingsPicker.${prefix}LimitNote`}
                values={{ max: MAX_RELATED_ITEMS }}
              />
            </p>
          ) : null}
          {!isExpanded && items.length > VISIBLE_PICKER_ROWS ? (
            <button type="button" className={css.showAllButton} onClick={() => setExpanded(true)}>
              <FormattedMessage
                id={`RelatedListingsPicker.${prefix}ShowAll`}
                values={{ count: items.length }}
              />
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

RelatedListingsPicker.defaultProps = {
  items: [],
};

RelatedListingsPicker.propTypes = {
  // The Final Form field holding the picked listing IDs
  name: string.isRequired,
  kind: oneOf([KIND_WORKS, KIND_SERVICES]).isRequired,
  // Pickable listings, newest first
  items: array,
  status: oneOf(['loading', 'loaded', 'error']).isRequired,
};

export default RelatedListingsPicker;
